import React, { useCallback } from 'react';

const getModePlayers = (actMode) => {
  if (actMode === '16') {
    return ['A', 'B', 'C', 'D'];
  }
  if (actMode === '12') {
    return ['A', 'B', 'C'];
  }
  return ['A', 'B'];
};

const getDefaultAssignments = (actMode) => {
  if (actMode === '16') {
    return ['D', 'A', 'D', 'A', 'B', 'C', 'B', 'C'];
  }
  if (actMode === '12') {
    return ['A', 'B', 'C', 'A', 'B', 'C'];
  }
  return ['A', 'B', 'A', 'B'];
};

// Helper function to swap or move assignments
const swapOrMove = (assignments, teamId, cup, fromIdx, toIdx) => {
  if (fromIdx === toIdx) return assignments;

  const newAssignments = { ...assignments };
  if (!newAssignments[teamId]) newAssignments[teamId] = {};
  if (!newAssignments[teamId][cup]) {
    newAssignments[teamId][cup] = ['A', 'B', 'A', 'B'];
  }

  const teamCupRow = [...newAssignments[teamId][cup]];
  const fromPlayer = teamCupRow[fromIdx];
  const toPlayer = teamCupRow[toIdx];

  if (toPlayer === null) {
    teamCupRow[toIdx] = fromPlayer;
    teamCupRow[fromIdx] = null;
  } else {
    teamCupRow[toIdx] = fromPlayer;
    teamCupRow[fromIdx] = toPlayer;
  }

  newAssignments[teamId][cup] = teamCupRow;
  return newAssignments;
};

const DraggableRaceInput = ({
  value,
  onChange,
  onKeyDown,
  inputRef,
  placeholder,
  cup,
  team,
  raceIndex,
  assignments,
  onAssignmentsChange,
  actMode = '8',
  className,
  style = {}
}) => {
  const modePlayers = getModePlayers(actMode);
  const defaultAssignments = getDefaultAssignments(actMode);

  const getDefaultPlayer = () => defaultAssignments[raceIndex] || modePlayers[raceIndex % modePlayers.length];

  const currentPlayer = assignments[team]?.[cup]?.[raceIndex] || getDefaultPlayer();
  const isPlayerA = currentPlayer === 'A';
  const isPlayerC = currentPlayer === 'C';
  const isPlayerD = currentPlayer === 'D';

  const handlePlayerKeyDown = useCallback((e) => {
    if (e.key === 'h' || e.key === 'H') {
      e.preventDefault();
      e.stopPropagation();

      const currentIndex = modePlayers.indexOf(currentPlayer);
      const newPlayer = modePlayers[(currentIndex + 1) % modePlayers.length];

      const newAssignments = { ...assignments };
      if (!newAssignments[team]) newAssignments[team] = {};
      if (!newAssignments[team][cup]) {
        newAssignments[team][cup] = [...defaultAssignments];
      }

      const newRow = [...newAssignments[team][cup]];
      newRow[raceIndex] = newPlayer;
      newAssignments[team][cup] = newRow;

      onAssignmentsChange(newAssignments);
      return;
    }

    if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      e.stopPropagation();

      const direction = e.key === 'ArrowLeft' ? -1 : 1;
      const targetIndex = raceIndex + direction;
      const maxRaces = modePlayers.length * 2;

      if (targetIndex >= 0 && targetIndex < maxRaces) {
        const newAssignments = swapOrMove(assignments, team, cup, raceIndex, targetIndex);
        onAssignmentsChange(newAssignments);
      }
    }
  }, [assignments, cup, currentPlayer, defaultAssignments, modePlayers, onAssignmentsChange, raceIndex, team]);

  const enhancedStyle = {
    ...style,
    border: 'none',
    outline: 'none',
    cursor: 'text',
    ...(isPlayerA ? { backgroundColor: '#ffeb3b66' } : {}),
    ...(isPlayerC ? { backgroundColor: '#87ceeb66' } : {}),
    ...(isPlayerD ? { backgroundColor: '#86efac66' } : {})
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <input
        ref={inputRef}
        className={className}
        style={enhancedStyle}
        value={value}
        onChange={onChange}
        onKeyDown={(e) => {
          handlePlayerKeyDown(e);
          if (!e.defaultPrevented && onKeyDown) {
            onKeyDown(e);
          }
        }}
        placeholder={placeholder}
      />
    </div>
  );
};

export default DraggableRaceInput;
export { swapOrMove };
